import { useContext, useState } from "react";
import {
  actionModeSet,
  deleteItemSet,
  IStudent,
  selectAllSet,
  selectDeleteItem,
  selectSelectAll,
  selectStudentEntities,
  selectStudents,
  selectStudentsActionMode,
  updateItemSet,
  updateStudents,
} from "features/Students";
import TableBody from "@mui/material/TableBody";
import EditIcon from "components/SVGs/Edit";
import PasswordIcon from "components/SVGs/Password";
import DeleteIcon from "components/SVGs/Delete";
import ShareIcon from "components/SVGs/Share";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import TableRow from "@mui/material/TableRow";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import { styled } from "@mui/material/styles";
import { Checkbox, SvgIconProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import PasswordPopup from "./Password";
import { ActionType } from "features/common/Interfaces";
import { useAppDispatch, useAppSelector } from "app/hooks";
import EditStudentDetails from "../EditStudent";
import ConfirmMessage from "components/ConfirmMessage";
import {
  useDeleteStudentListMutation,
  useDeleteStudentMutation,
} from "app/services/students";
import useToast from "hooks/useToast";
import useSendQRcodes from "./useSendQRcodes";
import useDebounce from "hooks/useDebounce";
import { FireBaseContext } from "contexts/FireBase";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "rgb(255 255 255)",
    color: "rgb(62 77 91)",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    padding: "16px 20px",
  },
}));

const customStyles = {
  checkBox: {
    width: "16px",
    height: "16px",
  },
};

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& td, & th": {
    color: "rgba(105 123 140)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

type Props = {
  list: IStudent[];
  withCheckBox: boolean;
};

const TBody = ({ list, withCheckBox = false }: Props) => {
  const { logEvent } = useContext(FireBaseContext);
  const toast = useToast();
  const dispatch = useAppDispatch();
  const actionMode = useAppSelector(selectStudentsActionMode);
  const deleteItem = useAppSelector(selectDeleteItem);
  const selectedStudentList = useAppSelector(selectStudents).filter(
    (student) => student.selected
  );
  const [deleteStudent] = useDeleteStudentMutation();
  const [deleteStudentList] = useDeleteStudentListMutation();
  const selectAll = useAppSelector(selectSelectAll);
  const studentEntities = useAppSelector(selectStudentEntities);
  const { i18n, t } = useTranslation();
  const { starting } = useDebounce<React.MouseEvent<SVGSVGElement>>();
  const sendData = useSendQRcodes();
  const [passwordPopup, setPasswordPopup] = useState<{
    open: boolean;
    data: null | IStudent;
  }>({ open: false, data: null });

  const editStudentHandler = (item: IStudent) => {
    dispatch(updateItemSet(item));
    dispatch(actionModeSet(ActionType.update));
    logEvent("ClickOnEditStudent");
  };

  const deleteStudentHandler = (item: IStudent) => {
    dispatch(actionModeSet(ActionType.delete));
    dispatch(deleteItemSet(item));
    logEvent("ClickOnDeleteStudent");
  };
  const passwordHandler = (item: IStudent) => {
    setPasswordPopup({ open: true, data: item });
    logEvent("ClickOnGetStudentPin");
  };

  const sendQRcode = async (item: IStudent) => {
    if (item.email)
      await sendData([
        {
          student_id: item.id,
          student_name: item.name,
          recipient_email: item.email,
        },
      ]);
    logEvent("ClickOnShareStudentCredentials");
  };

  const actionList = (item: IStudent) => {
    return {
      ...item,
      sharePassword: (
        <ShareIcon
          onClick={starting(() => sendQRcode(item))}
          sx={{
            cursor: item.email ? "pointer" : "auto",
            "&:hover path": {
              fill: item.email
                ? "rgb(97, 194, 238)"
                : "rgb(181, 181, 181) !important",
            },
            "& path": {
              fill: item.email
                ? "rgb(105, 123, 140)"
                : "rgb(181, 181, 181) !important",
            },
            "&:active path": {
              fill: item.email
                ? "rgb(105, 123, 140)"
                : "rgb(181, 181, 181) !important",
            },
          }}
        />
      ),
      qrcode: (
        <PasswordIcon
          onClick={() => passwordHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
      edit: (
        <EditIcon
          onClick={() => editStudentHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
      delete: (
        <DeleteIcon
          onClick={() => deleteStudentHandler(item)}
          sx={{
            cursor: "pointer",
            "& path": {
              fill: "rgb(105, 123, 140)",
            },
            "&:hover path": {
              fill: "rgb(97, 194, 238)",
            },
            "&:active path": {
              fill: "rgb(105, 123, 140)",
            },
          }}
        />
      ),
    };
  };

  const enhancedList = list.map((item) => actionList(item));
  const align = i18n.dir() === "rtl" ? "right" : "left";
  const closeHandler = () => {
    setPasswordPopup({ open: false, data: null });
  };

  const deleteHandler = async (answer: "yes" | "no") => {
    if (answer === "yes") {
      try {
        if (deleteItem || selectedStudentList?.length) {
          const data = await (deleteItem
            ? deleteStudent(deleteItem.id).unwrap()
            : deleteStudentList(
                selectedStudentList.map((student) => student.id)
              ).unwrap());
          if (data?._statusCode === 200) {
            toast({
              label: t("success"),
              message: t(deleteItem ? "Student Deleted" : "Students Deleted"),
              rtl: i18n.dir() === "rtl",
              type: "success",
              // toastId: "classUpdated",
            });
          } else if ([400, 261, 306].includes(data?._statusCode)) {
            toast({
              label: t("error"),
              message: t("Cant' delete Student"),
              rtl: i18n.dir() === "rtl",
              type: "error",
              toastId: "classUpdated",
            });
          } else throw data;
        }
      } catch (error) {
        console.log({ error });
      }
      logEvent("ClickOnConfirmDeleteStudent");
    }
    dispatch(deleteItemSet(null));
    dispatch(actionModeSet(ActionType.list));
  };
  const deleteDescription = deleteItem ? (
    <>
      <div>{t("Do you want to delete student")}</div>
      <div style={{ fontWeight: "bold" }}>{deleteItem?.name}</div>
      <div>{t("On confirm data will be lost")}</div>
    </>
  ) : (
    <>
      <div style={{ lineHeight: "22px" }}>
        {t("Do you want to delete student list")}
      </div>
      <div>{t("On confirm students data will be lost")}</div>
    </>
  );

  const handleToggle = (item: IStudent) => {
    const {
      id,
      studentId,
      name,
      grade,
      gradeId,
      className,
      classId,
      password,
      email,
      code,
      selected,
    } = item;
    const updatedStudent = {
      id,
      studentId,
      name,
      grade,
      gradeId,
      className,
      classId,
      password,
      email,
      code,
      selected: !selected,
    };
    dispatch(updateStudents(updatedStudent));
    const updateStudentList = structuredClone(studentEntities);
    updateStudentList[id] = updatedStudent;
    const selectAllValue = Object.values(updateStudentList).reduce(
      (acc, stu) => (acc === undefined ? stu.selected : acc && stu.selected),
      undefined as boolean | undefined
    );
    if (selectAll !== selectAllValue && selectAllValue !== undefined)
      dispatch(selectAllSet(selectAllValue));
  };

  const renderCheckBox = (item: IStudent) => {
    return (
      <Checkbox
        key={item.id}
        onChange={() => handleToggle(item)}
        checked={item.selected}
        color="primary"
        icon={<CheckBoxOutlineBlankIcon sx={customStyles.checkBox} />}
        checkedIcon={<CheckBoxIcon sx={customStyles.checkBox} />}
      />
    );
  };

  return (
    <>
      <ConfirmMessage
        open={ActionType.delete === actionMode}
        onClose={deleteHandler}
        description={deleteDescription}
        title={t(deleteItem ? "Delete Student" : "Delete Student List")}
      />
      <EditStudentDetails
        open={actionMode === ActionType.update}
        onClose={() => dispatch(actionModeSet(ActionType.list))}
      />
      <PasswordPopup {...passwordPopup} onClose={closeHandler} />
      <TableBody>
        {enhancedList.map(
          (
            row: IStudent & {
              sharePassword: React.ReactElement<SvgIconProps>;
              qrcode: React.ReactElement<SvgIconProps>;
              edit: React.ReactElement<SvgIconProps>;
              delete: React.ReactElement<SvgIconProps>;
            }
          ) => (
            <StyledTableRow key={row.id}>
              {withCheckBox ? (
                <StyledTableCell sx={{ padding: 0, "& *": { padding: 0 } }}>
                  {renderCheckBox(row)}
                </StyledTableCell>
              ) : null}
              <StyledTableCell align={align} sx={{ width: "10%" }}>
                {row.studentId}
              </StyledTableCell>
              <StyledTableCell component="th" scope="row" align={align}>
                {row.name}
              </StyledTableCell>
              <StyledTableCell align={align} sx={{ width: "10%" }}>
                {row.email}
              </StyledTableCell>
              <StyledTableCell align="center" sx={{ width: "10%" }}>
                {row.sharePassword}
              </StyledTableCell>
              <StyledTableCell align="center">{row.qrcode}</StyledTableCell>
              <StyledTableCell align="center">{row.edit}</StyledTableCell>
              <StyledTableCell align="center">{row.delete}</StyledTableCell>
            </StyledTableRow>
          )
        )}
      </TableBody>
    </>
  );
};

export default TBody;
